import {
  AfterViewChecked,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChange,
  TemplateRef,
  ViewChildren,
  isDevMode,
  OnInit,
  OnDestroy,
} from '@angular/core';

import { LocalizationService, L10N_PREFIX } from '@progress/kendo-angular-l10n';
import { Keys } from '../common/keys';
import { PanelBarExpandMode } from './panelbar-expand-mode';
import { PanelBarItemComponent } from './panelbar-item.component';
import { PanelBarItemModel } from './panelbar-item-model';
import { PanelBarService } from './panelbar.service';
import { PanelBarItemTemplateDirective } from './panelbar-item-template.directive';
import util from '../common/util';
import { Subscription } from 'rxjs';
import { pxToRem } from '@app/shared/pipes/px-to-rem.pipe';

/**
 * Represents the Kendo UI PanelBar component for Angular.
 */
@Component({
  standalone: false,
  exportAs: 'kendoPanelbar',
  providers: [
    PanelBarService,
    LocalizationService,
    {
      provide: L10N_PREFIX,
      useValue: 'kendo.panelbar',
    },
  ],
  selector: 'kendo-panelbar',
  template: `
    <ng-content *ngIf="contentChildItems && !items" select="kendo-panelbar-item"></ng-content>
    <ng-template [ngIf]="items?.length">
      <kendo-panelbar-item
        *ngFor="let item of items"
        [title]="item.title"
        [id]="item.id"
        [icon]="item.icon"
        [iconClass]="item.iconClass"
        [imageUrl]="item.imageUrl"
        [selected]="!!item.selected"
        [expanded]="!!item.expanded"
        [disabled]="!!item.disabled"
        [template]="templateRef"
        [items]="item.children"
        [content]="item.content">
      </kendo-panelbar-item>
    </ng-template>
  `,
})

// TODO: add styles as input prop
export class PanelBarComponent implements AfterViewChecked, OnChanges, OnInit, OnDestroy {
  /**
   * Sets the expand mode of the PanelBar through the `PanelBarExpandMode` enum.
   *
   * The available modes are:
   * - `"single"`&mdash;Expands only one item at a time. Expanding an item collapses the item that was previously expanded.
   * - `"multiple"`&mdash;The default mode of the PanelBar.
   * Expands more than one item at a time. Items can also be toggled.
   * - `"full"`&mdash;Expands only one item at a time.
   * The expanded area occupies the entire height of the PanelBar. Requires you to set the `height` property.
   */
  @Input() public expandMode: number = PanelBarExpandMode.Default;

  /**
   * Allows the PanelBar to modify the selected state of the items.
   */
  @Input() public selectable: boolean = true;

  /**
   * Sets the animate state of the PanelBar.
   */
  @Input() public animate: boolean = true;

  /**
   * Sets the height of the component when the `"full"` expand mode is used.
   * This option is ignored in the `"multiple"` and `"single"` expand modes.
   */
  @Input() public height: any = pxToRem(400);

  /**
   * When set to `true`, the PanelBar renders the content of all items and they are persisted in the DOM.
   * By default, this option is set to `false`.
   */
  @Input()
  public get keepItemContent(): boolean {
    return this._keepItemContent;
  }
  public set keepItemContent(keepItemContent: boolean) {
    this._keepItemContent = keepItemContent;
    this.eventService.onKeepContent(keepItemContent);
  }

  /**
   * Sets the items of the PanelBar as an array of `PanelBarItemModel` instances.
   */
  @Input()
  public set items(data: PanelBarItemModel[]) {
    if (data) {
      this._items = util.parsePanelBarItems(data);
    }
  }
  public get items(): PanelBarItemModel[] {
    return this._items;
  }

  /**
   * Fires each time the user interacts with a PanelBar item.
   * The event data contains all items that are modified.
   */
  @Output() public stateChange: EventEmitter<any> = new EventEmitter();

  @HostBinding('attr.tabIndex') public tabIndex: number = 0;
  @HostBinding('attr.role') public role: string = 'tree';
  @HostBinding('attr.aria-activedescendant') public activeDescendant: string = '';

  @HostBinding('style.height') get hostHeight(): string {
    return this.expandMode === PanelBarExpandMode.Full ? this.height : 'auto';
  }

  @HostBinding('style.overflow') get overflow(): string {
    return this.expandMode === PanelBarExpandMode.Full ? 'hidden' : 'visible';
  }

  @HostBinding('attr.dir') get dir(): string {
    return this.localization.rtl ? 'rtl' : 'ltr';
  }

  @ContentChild(PanelBarItemTemplateDirective) public template: PanelBarItemTemplateDirective;
  @ContentChildren(PanelBarItemComponent, { descendants: true }) public contentItems: QueryList<PanelBarItemComponent>;
  @ContentChildren(PanelBarItemComponent) public contentChildItems: QueryList<PanelBarItemComponent>;
  @ViewChildren(PanelBarItemComponent) public viewChildItems: QueryList<PanelBarItemComponent>;

  /**
   * @hidden
   */
  public invertKeys(original: any, inverted: any): any {
    return this.localization.rtl ? inverted : original;
  }

  constructor(
    elementRef: ElementRef,
    eventService: PanelBarService<PanelBarItemComponent>,
    private localization: LocalizationService,
  ) {
    /* tslint:disable-line */
    this.keyBindings = this.computedKeys;
    this.elementRef = elementRef;
    this.eventService = eventService;
    this.eventService.children$.subscribe((event) => this.onItemAction(event));
  }

  public get computedKeys(): object {
    return {
      [Keys.space]: (): void => this.selectFocusedItem(),
      [Keys.enter]: (): void => this.selectFocusedItem(),
      [Keys.up]: (): void => this.focusPreviousItem(),
      [this.invertKeys(Keys.left, Keys.right)]: (): void => this.collapseItem(),
      [Keys.down]: (): void => this.focusNextItem(),
      [this.invertKeys(Keys.right, Keys.left)]: (): void => this.expandItem(),
      [Keys.end]: (): void => this.focusLastItem(),
      [Keys.home]: (): void => this.focusFirstItem(),
    };
  }

  public ngOnDestroy(): void {
    if (this.localizationChangeSubscription) {
      this.localizationChangeSubscription.unsubscribe();
    }
  }

  public ngOnInit(): void {
    this.localizationChangeSubscription = this.localization.changes.subscribe(
      () => (this.keyBindings = this.computedKeys),
    );
  }

  public ngAfterViewChecked(): void {
    if (this.items) {
      this.childrenItems = this.viewChildItems.toArray();
      this.allItems = this.viewItems;
    } else {
      this.childrenItems = this.contentChildItems.toArray();
      this.allItems = this.contentItems.toArray();
    }

    if (this.isViewInit && this.childrenItems.length) {
      this.isViewInit = false;
      setTimeout(() => this.updateChildrenHeight());
    }

    this.validateConfiguration();
  }

  public ngOnChanges(changes: { [propertyName: string]: SimpleChange }): void {
    // tslint:disable-line
    if (changes['height'] || changes['expandMode'] || changes['items']) {
      // tslint:disable-line
      if (this.childrenItems) {
        setTimeout(this.updateChildrenHeight);
      }
    }
  }

  public get templateRef(): TemplateRef<any> {
    return this.template ? this.template.templateRef : undefined;
  }

  /**
   * @hidden
   */
  @HostListener('click', ['$event'])
  public onComponentClick(event: any): void {
    const itemClicked: boolean = this.visibleItems().some((item: PanelBarItemComponent) => {
      return item.header.nativeElement.contains(event.target);
    });

    if (!this.focused && itemClicked) {
      this.elementRef.nativeElement.focus();
    }
  }

  /**
   * @hidden
   */
  @HostListener('focus')
  public onComponentFocus(): void {
    this.eventService.onFocus();

    this.focused = true;

    if (this.allItems.length > 0) {
      const visibleItems: PanelBarItemComponent[] = this.visibleItems();
      const focusedItems: PanelBarItemComponent[] = visibleItems.filter((item) => item.focused);

      if (!focusedItems.length && visibleItems.length > 0) {
        visibleItems[0].focused = true;
        this.activeDescendant = visibleItems[0].itemId;
      }
    }

    if (this.animate) {
      this.updateAnimate();
    }
  }

  /**
   * @hidden
   */
  @HostListener('blur')
  public onComponentBlur(): void {
    this.eventService.onBlur();

    this.focused = false;

    this.activeDescendant = '';
  }

  /**
   * @hidden
   */
  @HostListener('keydown', ['$event'])
  public onComponentKeyDown(event: any): void {
    if (event.target === this.elementRef.nativeElement) {
      if (
        event.keyCode === Keys.space ||
        event.keyCode === Keys.up ||
        event.keyCode === Keys.down ||
        event.keyCode === Keys.left ||
        event.keyCode === Keys.right ||
        event.keyCode === Keys.home ||
        event.keyCode === Keys.end ||
        event.keyCode === Keys['page up'] ||
        event.keyCode === Keys['page down']
      ) {
        event.preventDefault();
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
      const handler: Function = this.keyBindings[event.keyCode];

      // TODO: check if next item is disabled and skip operation?
      if (handler) {
        handler();
      }
    }
  }

  private localizationChangeSubscription: Subscription;

  private get viewItems(): PanelBarItemComponent[] {
    let treeItems: PanelBarItemComponent[] = [];

    this.viewChildItems.toArray().forEach((item) => {
      treeItems.push(item);
      treeItems = treeItems.concat(item.subTreeViewItems());
    });

    return treeItems;
  }

  private allItems: PanelBarItemComponent[];
  private childrenItems: PanelBarItemComponent[];
  private isViewInit: boolean = true;
  private focused: boolean = false;

  private _items: PanelBarItemModel[];
  private _keepItemContent: boolean = false;

  private elementRef: ElementRef;
  private eventService: PanelBarService<PanelBarItemComponent>;
  private keyBindings: object;

  private validateConfiguration(): void {
    if (isDevMode()) {
      if (this.items && this.contentItems && this.contentItems.length > 0) {
        throw new Error('Invalid configuration: mixed template components and items property.');
      }
    }
  }

  private updateChildrenHeight: Function = () => {
    let childrenHeight: number = 0;
    const panelbarHeight: number = this.elementRef.nativeElement.offsetHeight;
    const contentOverflow: string = this.expandMode === PanelBarExpandMode.Full ? 'auto' : 'visible';

    this.childrenItems.forEach((item) => {
      childrenHeight += item.headerHeight();
    });

    this.childrenItems.forEach((item) => {
      item.contentHeight =
        PanelBarExpandMode.Full === this.expandMode ? panelbarHeight - childrenHeight + 'px' : 'auto';
      item.contentOverflow = contentOverflow;
    });
  };

  private updateAnimate: Function = () => {
    this.allItems.forEach((item) => {
      if (item.state === 'activeWithoutAnimation') {
        item.state = 'active';
      }
    });
  };

  private onItemAction(item: PanelBarItemComponent): void {
    if (!item) {
      return;
    }

    let modifiedItems: PanelBarItemComponent[] = new Array<PanelBarItemComponent>();

    this.allItems.forEach((currentItem: PanelBarItemComponent) => {
      let selectedState: boolean = currentItem === item;
      let focusedState: boolean = selectedState;

      selectedState = this.selectable ? selectedState : currentItem.selected;

      if (currentItem.selected !== selectedState || currentItem.focused !== focusedState) {
        currentItem.selected = selectedState;
        currentItem.focused = focusedState;
        this.activeDescendant = focusedState ? currentItem.itemId : '';

        modifiedItems.push(currentItem);
      }
    });

    if (this.expandMode === PanelBarExpandMode.Multiple) {
      if (item.hasChildItems || item.hasContent) {
        item.animate = this.animate;
        item.expanded = !item.expanded;
      }

      if (modifiedItems.indexOf(item) < 0) {
        modifiedItems.push(item);
      }
    } else {
      let siblings: PanelBarItemComponent[] = item.parent ? item.parent.childrenItems : this.childrenItems;

      if (item.hasChildItems || item.hasContent) {
        siblings.forEach((currentItem: PanelBarItemComponent) => {
          let expandedState: boolean = currentItem === item;

          if (currentItem.expanded !== expandedState) {
            currentItem.animate = this.animate;
            currentItem.expanded = expandedState;

            if (modifiedItems.indexOf(currentItem) < 0) {
              modifiedItems.push(currentItem);
            }
          }
        });
      }
    }

    if (modifiedItems.length > 0) {
      this.stateChange.emit(modifiedItems.map((currentItem) => currentItem.serialize()));
    }
  }

  @HostBinding('class.k-widget')
  @HostBinding('class.k-panelbar')
  public get hostClasses(): boolean {
    return true;
  }

  private isVisible(item: PanelBarItemComponent): boolean {
    const visibleItems: PanelBarItemComponent[] = this.visibleItems();

    return visibleItems.some((i) => i === item);
  }

  private getVisibleParent(item: PanelBarItemComponent): PanelBarItemComponent {
    const visibleItems: PanelBarItemComponent[] = this.visibleItems();

    if (!item.parent) {
      return item;
    }

    return visibleItems.some((i) => i === item.parent) ? item.parent : this.getVisibleParent(item.parent);
  }

  private focusItem(action: string): void {
    const visibleItems: PanelBarItemComponent[] = this.visibleItems();

    let currentIndex: number = visibleItems.findIndex((item) => item.focused);
    let currentItem: PanelBarItemComponent = visibleItems[currentIndex];
    let nextItem: PanelBarItemComponent;

    if (currentIndex === -1) {
      let focusedItem: PanelBarItemComponent = this.allItems.find((item) => item.focused);

      focusedItem.focused = false;
      currentItem = this.getVisibleParent(focusedItem);
      currentIndex = visibleItems.findIndex((item) => item === currentItem);
    }

    switch (action) {
      case 'lastItem':
        nextItem = visibleItems[visibleItems.length - 1];
        break;
      case 'firstItem':
        nextItem = visibleItems[0];
        break;
      case 'nextItem':
        nextItem = visibleItems[currentIndex < visibleItems.length - 1 ? currentIndex + 1 : 0];
        break;
      case 'previousItem':
        nextItem = visibleItems[currentIndex > 0 ? currentIndex - 1 : visibleItems.length - 1];
        break;
      default:
    }

    if (currentItem && nextItem && currentItem !== nextItem) {
      this.moveFocus(currentItem, nextItem);
    }
  }

  private moveFocus(from: PanelBarItemComponent, to: PanelBarItemComponent): void {
    from.focused = false;
    to.focused = true;
    this.activeDescendant = to.itemId;

    const modifiedItems: object[] = new Array<object>(from.serialize(), to.serialize());

    this.stateChange.emit(modifiedItems);
  }

  private focusLastItem(): void {
    this.focusItem('lastItem');
  }

  private focusFirstItem(): void {
    this.focusItem('firstItem');
  }

  private focusNextItem(): void {
    this.focusItem('nextItem');
  }

  private focusPreviousItem(): void {
    this.focusItem('previousItem');
  }

  private expandItem(): void {
    let currentItem = this.allItems.filter((item) => item.focused)[0];

    if (!this.isVisible(currentItem)) {
      currentItem.focused = false;
      currentItem = this.getVisibleParent(currentItem);
    }

    if (currentItem.hasChildItems || currentItem.hasContent) {
      if (!currentItem.expanded) {
        this.onItemAction(currentItem);
      } else if (currentItem.hasChildItems) {
        const firstChildIndex = currentItem.childrenItems.findIndex((item) => !item.disabled);

        if (firstChildIndex > -1) {
          this.moveFocus(currentItem, currentItem.childrenItems[firstChildIndex]);
        }
      }
    }
  }

  private collapseItem(): void {
    const currentItem = this.allItems.filter((item) => item.focused)[0];

    if (currentItem.expanded) {
      this.onItemAction(currentItem);
    } else if (currentItem.parent) {
      this.moveFocus(currentItem, currentItem.parent);
    }
  }

  private selectFocusedItem(): void {
    let focusedItem: PanelBarItemComponent = this.allItems.filter((item) => item.focused)[0];

    if (!this.isVisible(focusedItem)) {
      focusedItem.focused = false;
      focusedItem = this.getVisibleParent(focusedItem);
    }

    if (focusedItem) {
      focusedItem.onItemAction();
    }
  }

  private visibleItems(): PanelBarItemComponent[] {
    return this.flatVisibleItems(this.childrenItems);
  }

  private flatVisibleItems(
    listOfItems: PanelBarItemComponent[] = new Array<PanelBarItemComponent>(),
    flattedItems: PanelBarItemComponent[] = new Array<PanelBarItemComponent>(),
  ): PanelBarItemComponent[] {
    listOfItems.forEach((item) => {
      if (!item.disabled) {
        flattedItems.push(item);
        if (item.expanded && item.hasChildItems) {
          this.flatVisibleItems(item.childrenItems, flattedItems);
        }
      }
    });

    return flattedItems;
  }
}
